/**
 * Planner · Horarios (A1) — CRUD de horarios oficiales recurrentes (Schedule) y
 * parte de horas (timesheet) con marcaje REAL vs CONTABLE por día.
 *
 * Un horario define, por día de la semana, hasta varios tramos {entrada
 * planificada, salida forzosa, entrada contable?}. La salida forzosa la usa el
 * auto clock-out de gk2control cuando el recurso tiene este horario asignado.
 * El redondeo es opt-in por organización (config gk2control). Gated por
 * planner:manage (editar) / planner:read (ver).
 */
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { PlusIcon, TrashIcon, PencilSquareIcon, ClockIcon } from '@heroicons/react/24/outline';
import { LoadingSpinner } from '@/components/common';
import {
  plannerApi,
  type PlannerSchedule,
  type PlannerResource,
  type ScheduleDay,
  type ScheduleSegment,
  type TimesheetResponse,
} from '@/services/api';
import { useOrganizationStore } from '@/stores/organization';

const inputClass = 'w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

// Días en orden de presentación (Lun→Dom); valores JS 0=Dom … 6=Sáb.
const WEEKDAYS: { value: number; label: string }[] = [
  { value: 1, label: 'Lunes' }, { value: 2, label: 'Martes' }, { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' }, { value: 5, label: 'Viernes' }, { value: 6, label: 'Sábado' }, { value: 0, label: 'Domingo' },
];

const fmtTime = (iso: string | null, tz: string) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleTimeString('es-ES', { timeZone: tz, hour: '2-digit', minute: '2-digit' }); }
  catch { return '—'; }
};
const fmtMinutes = (m: number | null) => {
  if (m == null) return '—';
  const h = Math.floor(m / 60), min = m % 60;
  return `${h}h ${String(min).padStart(2, '0')}m`;
};
const OUT_SOURCE_LABEL: Record<string, string> = { auto: 'Auto', manual: 'Manual', gk2control: 'gk2', schedule: 'Horario' };

export default function SchedulesTab({ canManage }: { canManage: boolean }) {
  const selectedOrgId = useOrganizationStore((s) => s.selectedOrg?._id);
  const [schedules, setSchedules] = useState<PlannerSchedule[]>([]);
  const [resources, setResources] = useState<PlannerResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<PlannerSchedule | 'new' | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        plannerApi.listSchedules(),
        plannerApi.getResources().catch(() => ({ data: { data: [] } })),
      ]);
      setSchedules((s.data.data || []) as PlannerSchedule[]);
      setResources((r.data.data || []) as PlannerResource[]);
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status !== 403) toast.error('No se pudieron cargar los horarios');
      setSchedules([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [selectedOrgId]);

  const resourceName = (id?: string | null) => (id ? (resources.find((r) => r._id === id)?.name || '(recurso)') : '— plantilla');

  const remove = async (s: PlannerSchedule) => {
    if (!confirm(`¿Eliminar el horario "${s.name}"?`)) return;
    try { await plannerApi.removeSchedule(s._id); toast.success('Horario eliminado'); void load(); }
    catch { toast.error('No se pudo eliminar'); }
  };

  return (
    <div className="space-y-8">
      {/* --- Horarios oficiales --- */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><ClockIcon className="w-5 h-5 text-vida-600" /> Horarios oficiales</h2>
          {canManage && (
            <button onClick={() => setEditing('new')} className="inline-flex items-center gap-1.5 px-3 py-2 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700">
              <PlusIcon className="w-5 h-5" /> Nuevo horario
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><LoadingSpinner /></div>
        ) : schedules.length === 0 ? (
          <p className="text-sm text-gray-400">Aún no hay horarios. Crea uno y asígnalo a un recurso para activar la salida forzosa.</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {schedules.map((s) => (
              <div key={s._id} className="bg-white rounded-lg border border-gray-200 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{s.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{resourceName(s.resourceId)} · {s.timezone}{!s.active && ' · inactivo'}</p>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => setEditing(s)} className="p-1 text-gray-400 hover:text-vida-600"><PencilSquareIcon className="w-4 h-4" /></button>
                      <button onClick={() => void remove(s)} className="p-1 text-gray-400 hover:text-red-600"><TrashIcon className="w-4 h-4" /></button>
                    </div>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {WEEKDAYS.filter((w) => (s.days || []).some((d) => d.weekday === w.value && d.segments.length)).map((w) => (
                    <span key={w.value} className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{w.label.slice(0, 3)}</span>
                  ))}
                  {s.rounding && <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-700">redondeo {s.rounding.intervalMin}′/{s.rounding.marginMin}′</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* --- Parte de horas (timesheet) --- */}
      <TimesheetPanel />

      {editing && (
        <ScheduleEditor
          schedule={editing === 'new' ? null : editing}
          resources={resources}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }}
        />
      )}
    </div>
  );
}

// ---------- Editor de horario ----------

function ScheduleEditor({ schedule, resources, onClose, onSaved }: {
  schedule: PlannerSchedule | null; resources: PlannerResource[]; onClose: () => void; onSaved: () => void;
}) {
  const isEdit = !!schedule;
  const [name, setName] = useState(schedule?.name || '');
  const [timezone, setTimezone] = useState(schedule?.timezone || 'Europe/Madrid');
  const [active, setActive] = useState(schedule?.active ?? true);
  const [resourceId, setResourceId] = useState(schedule?.resourceId || '');
  const [days, setDays] = useState<ScheduleDay[]>(schedule?.days ? schedule.days.map((d) => ({ ...d, segments: d.segments.map((s) => ({ ...s })) })) : []);
  const [busy, setBusy] = useState(false);

  const segmentsOf = (weekday: number): ScheduleSegment[] => days.find((d) => d.weekday === weekday)?.segments || [];
  const setSegments = (weekday: number, segs: ScheduleSegment[]) => {
    setDays((prev) => {
      const others = prev.filter((d) => d.weekday !== weekday);
      return segs.length ? [...others, { weekday, segments: segs }] : others;
    });
  };
  const addSegment = (weekday: number) => setSegments(weekday, [...segmentsOf(weekday), { plannedIn: '09:00', forcedOut: '17:00' }]);
  const updateSegment = (weekday: number, i: number, patch: Partial<ScheduleSegment>) =>
    setSegments(weekday, segmentsOf(weekday).map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const removeSegment = (weekday: number, i: number) => setSegments(weekday, segmentsOf(weekday).filter((_, idx) => idx !== i));

  const save = async () => {
    if (!name.trim()) { toast.error('Ponle un nombre al horario'); return; }
    const hhmm = /^\d{1,2}:\d{2}$/;
    for (const d of days) for (const s of d.segments) {
      if (!hhmm.test(s.plannedIn) || !hhmm.test(s.forcedOut)) { toast.error('Revisa las horas (formato HH:mm)'); return; }
    }
    setBusy(true);
    try {
      const payload = {
        name: name.trim(), timezone, active,
        resourceId: resourceId || null,
        days: days.filter((d) => d.segments.length),
      };
      if (isEdit && schedule) await plannerApi.updateSchedule(schedule._id, payload);
      else await plannerApi.createSchedule(payload);
      toast.success(isEdit ? 'Horario guardado' : 'Horario creado');
      onSaved();
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'No se pudo guardar el horario';
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm w-full max-w-2xl p-6 max-h-[92vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{isEdit ? 'Editar horario' : 'Nuevo horario'}</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><label className={labelClass}>Nombre</label><input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div>
            <label className={labelClass}>Recurso (opcional)</label>
            <select className={inputClass} value={resourceId} onChange={(e) => setResourceId(e.target.value)}>
              <option value="">— plantilla (sin asignar)</option>
              {resources.map((r) => <option key={r._id} value={r._id}>{r.name}{r.employeeNif ? ` · ${r.employeeNif}` : ''}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Zona horaria</label>
            <input className={inputClass} value={timezone} onChange={(e) => setTimezone(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 sm:col-span-2">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="rounded border-gray-300 text-vida-600 focus:ring-vida-500" /> Activo
          </label>
        </div>

        <div className="mt-5">
          <p className="text-sm font-semibold text-gray-900 mb-2">Tramos por día</p>
          <p className="text-xs text-gray-400 mb-3">Cada tramo: entrada planificada + salida forzosa (mañana y tarde = 2 tramos). La salida forzosa es la hora a la que el sistema cierra el fichaje si no hubo salida.</p>
          <div className="space-y-2">
            {WEEKDAYS.map((w) => {
              const segs = segmentsOf(w.value);
              return (
                <div key={w.value} className="border border-gray-200 rounded-lg p-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">{w.label}</span>
                    <button onClick={() => addSegment(w.value)} className="inline-flex items-center gap-1 text-xs text-vida-700 hover:bg-vida-50 px-2 py-1 rounded"><PlusIcon className="w-3.5 h-3.5" /> Tramo</button>
                  </div>
                  {segs.length === 0 ? (
                    <p className="text-xs text-gray-300 mt-1">Libre</p>
                  ) : (
                    <div className="mt-2 space-y-1.5">
                      {segs.map((s, i) => (
                        <div key={i} className="flex items-center gap-2 flex-wrap">
                          <label className="text-[11px] text-gray-500">Entra</label>
                          <input type="time" className="rounded border-gray-300 text-sm py-1" value={s.plannedIn} onChange={(e) => updateSegment(w.value, i, { plannedIn: e.target.value })} />
                          <label className="text-[11px] text-gray-500">Salida forzosa</label>
                          <input type="time" className="rounded border-gray-300 text-sm py-1" value={s.forcedOut} onChange={(e) => updateSegment(w.value, i, { forcedOut: e.target.value })} />
                          <button onClick={() => removeSegment(w.value, i)} className="p-1 text-gray-300 hover:text-red-600"><TrashIcon className="w-3.5 h-3.5" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancelar</button>
          <button onClick={save} disabled={busy} className="px-4 py-2 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700 disabled:opacity-50">{busy ? 'Guardando…' : 'Guardar'}</button>
        </div>
      </div>
    </div>
  );
}

// ---------- Parte de horas (real vs contable) ----------

function TimesheetPanel() {
  const today = new Date();
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const monthAgo = new Date(today.getTime() - 30 * 24 * 3600_000);

  const [nif, setNif] = useState('');
  const [from, setFrom] = useState(iso(monthAgo));
  const [to, setTo] = useState(iso(today));
  const [data, setData] = useState<TimesheetResponse | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!nif.trim()) { toast.error('Introduce el NIF del empleado'); return; }
    setBusy(true);
    try {
      const r = await plannerApi.getTimesheet({ nif: nif.trim(), from: `${from}T00:00:00.000Z`, to: `${to}T23:59:59.999Z` });
      setData(r.data.data as TimesheetResponse);
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'No se pudo cargar el parte';
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-3">Parte de horas (real vs contable)</h2>
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div><label className={labelClass}>NIF</label><input className={inputClass} value={nif} onChange={(e) => setNif(e.target.value)} placeholder="12345678A" /></div>
        <div><label className={labelClass}>Desde</label><input type="date" className={inputClass} value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div><label className={labelClass}>Hasta</label><input type="date" className={inputClass} value={to} onChange={(e) => setTo(e.target.value)} /></div>
        <button onClick={run} disabled={busy} className="px-4 py-2 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700 disabled:opacity-50">{busy ? 'Cargando…' : 'Ver parte'}</button>
      </div>

      {data && (
        <>
          <div className="flex flex-wrap gap-2 mb-2 text-xs">
            <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600">{data.timezone}</span>
            <span className={`px-2 py-0.5 rounded ${data.rounding ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-500'}`}>{data.rounding ? 'con redondeo' : 'sin redondeo'}</span>
            <span className={`px-2 py-0.5 rounded ${data.hasSchedule ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{data.hasSchedule ? 'con horario' : 'sin horario'}</span>
          </div>
          {data.days.length === 0 ? (
            <p className="text-sm text-gray-400">Sin fichajes en el rango.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-3">Día</th>
                    <th className="py-2 px-3">Entrada real</th>
                    <th className="py-2 px-3">Salida real</th>
                    <th className="py-2 px-3">Entrada contable</th>
                    <th className="py-2 px-3">Salida contable</th>
                    <th className="py-2 px-3">Fuente salida</th>
                    <th className="py-2 pl-3">Horas</th>
                  </tr>
                </thead>
                <tbody>
                  {data.days.map((d) => (
                    <tr key={d.date} className="border-b border-gray-100">
                      <td className="py-2 pr-3 font-medium text-gray-700 whitespace-nowrap">{d.date}</td>
                      <td className="py-2 px-3 text-gray-500">{fmtTime(d.rawIn, data.timezone)}</td>
                      <td className="py-2 px-3 text-gray-500">{fmtTime(d.rawOut, data.timezone)}</td>
                      <td className="py-2 px-3 text-gray-900 font-medium">{fmtTime(d.accountableIn, data.timezone)}</td>
                      <td className="py-2 px-3 text-gray-900 font-medium">{fmtTime(d.accountableOut, data.timezone)}</td>
                      <td className="py-2 px-3">
                        {d.outSource && <span className={`text-[10px] px-1.5 py-0.5 rounded ${d.outSource === 'schedule' ? 'bg-amber-100 text-amber-700' : d.outSource === 'auto' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>{OUT_SOURCE_LABEL[d.outSource] || d.outSource}</span>}
                      </td>
                      <td className="py-2 pl-3 text-gray-700">{fmtMinutes(d.accountableMinutes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
